import React, { useEffect, useState } from 'react';
import LayoutComponent from '../BaseScreen';
import { useData } from '../../Context/DataContext';
import { useNavigate } from 'react-router-dom';
import { useIntake } from '../../Context/IntakeContext';
import { useHistoric } from '../../Context/HistoricContext';
import { ArrowLeft, Calendar, Clock, FileText, Hash, Activity } from 'react-feather';

const CensusUserScreen = () => {
  const navigate = useNavigate();
  const { patientDetails } = useData();
  const { grabFullAvailityData } = useIntake();
  const { grabUserFinancialRecords } = useHistoric();

  const [localPatientDetails, setLocalPatientDetails] = useState<any>(patientDetails);

  useEffect(() => {
    grabUserFinancialRecords(patientDetails.policy_no);
  }, [patientDetails]);

  useEffect(() => {
    setLocalPatientDetails(patientDetails);
  }, [patientDetails]);

  function formatDate(date: string) {
    const nextDate = new Date(date);
    const pstOffset = 8 * 60;
    const utcOffset = nextDate.getTimezoneOffset();
    nextDate.setMinutes(nextDate.getMinutes() + utcOffset - pstOffset);
    nextDate.setDate(nextDate.getDate() + 1);
    const year = nextDate.getFullYear();
    const month = String(nextDate.getMonth() + 1).padStart(2, '0');
    const day = String(nextDate.getDate()).padStart(2, '0');
    return `${month}-${day}-${year}`;
  }

  function convertDateToCustomFormatDate(date: Date) {
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    const yyyy = date.getUTCFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }

  function convertDateToCustomFormat(dateStr: string) {
    const date = new Date(dateStr);
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    const yyyy = date.getUTCFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }

  const redirectToVerify = (policy_id: string) => {
    grabFullAvailityData(
      patientDetails.insurance_name,
      convertDateToCustomFormatDate(new Date()),
      policy_id,
      convertDateToCustomFormat(patientDetails.date_of_birth),
      `${patientDetails.first_name} ${patientDetails.last_name}`
    );
    navigate(`/verifytx/census/${policy_id}`);
  };

  const statusPillClass = (status: string) => {
    if (status === 'Approved') return 'bg-green-500/15 text-green-300 border-green-500/30';
    if (status === 'Pending') return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
    if (status === 'Submitted') return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    return 'bg-gray-500/15 text-gray-300 border-gray-500/30';
  };

  return (
    <LayoutComponent
      header={
        <div className="w-full h-full bg-dark-card border border-dark-border rounded-xl px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/census')}
              className="p-2 rounded-lg hover:bg-dark-input border border-transparent hover:border-dark-border transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-400" />
            </button>
            <div className="flex flex-col">
              <div className="text-white font-semibold text-lg">
                {patientDetails.first_name} {patientDetails.last_name}
              </div>
              <div className="text-xs text-gray-400 -mt-0.5">
                {patientDetails.insurance_name} • {patientDetails.policy_no}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center px-3 py-1.5 rounded-full border text-xs font-medium ${statusPillClass(
                patientDetails.auth_status || ''
              )}`}
            >
              {patientDetails.auth_status || 'Unknown'}
            </span>
            <button
              onClick={() => redirectToVerify(patientDetails.policy_no)}
              className="px-4 py-2 rounded-xl bg-accent-teal text-black text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Verify Tx
            </button>
          </div>
        </div>
      }
      content={
        <div className="w-full h-full overflow-auto px-4 pb-6">
          {/* Patient Info Cards */}
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={14} className="text-gray-400" />
                <span className="text-xs text-gray-400">Days Remaining</span>
              </div>
              <div className="text-2xl font-semibold text-white">{patientDetails.days_left || 0}</div>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity size={14} className="text-gray-400" />
                <span className="text-xs text-gray-400">Level of Care</span>
              </div>
              <div className="text-2xl font-semibold text-white">{patientDetails.level_of_care || '—'}</div>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Hash size={14} className="text-gray-400" />
                <span className="text-xs text-gray-400">CPT Code</span>
              </div>
              <div className="text-2xl font-semibold text-white">{patientDetails.cpt_code || '—'}</div>
            </div>
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={14} className="text-gray-400" />
                <span className="text-xs text-gray-400">Date of Birth</span>
              </div>
              <div className="text-2xl font-semibold text-white">{formatDate(patientDetails.date_of_birth)}</div>
            </div>
          </div>

          {/* Current Visit Card */}
          <div className="mt-4">
            <div className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <FileText size={16} className="text-accent-teal" />
                <h3 className="text-white font-semibold">Current Visit Details</h3>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <span className="text-xs text-gray-400">Insurance</span>
                  <p className="text-white mt-1">{patientDetails.insurance_name}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Policy Number</span>
                  <p className="text-white mt-1">{patientDetails.policy_no}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Gender</span>
                  <p className="text-white mt-1">{patientDetails.gender || '—'}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Facility</span>
                  <p className="text-white mt-1">{patientDetails.facility || '—'}</p>
                </div>
              </div>

              {localPatientDetails.diagnosis && localPatientDetails.diagnosis.length > 0 && (
                <div className="mt-4 pt-4 border-t border-dark-border">
                  <span className="text-xs text-gray-400">Current Diagnosis</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {localPatientDetails.diagnosis.map((singleCode: string, idx: number) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-lg bg-dark-input border border-dark-border text-sm text-gray-300"
                      >
                        {singleCode}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default CensusUserScreen;
